import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useRouter } from "next/router"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  cn,
  Skeleton,
  TABLE_CARD_CLASS,
} from "@coval/ui"

import { openCoviWithPrompt } from "@/lib/coviEvents"
import {
  isKeyboardTypingTarget,
  isModalLayerTarget,
} from "@/lib/isKeyboardTypingTarget"
import type { TestSetRecord } from "@/types/api/TestSetRecord"
import { buildWorkspaceAwareNavigation } from "@/utils/workspaceRoutes"

import { FindReplaceProvider } from "../contexts/FindReplaceContext"
import {
  TestSetEditorProvider,
  useTestSetEditorContext,
} from "../contexts/TestSetEditorContext"
import { TestSetSaveProvider } from "../contexts/TestSetSaveContext"
import { EVENTS, useEventBus, useEventDispatcher } from "../event-bus"
import { useTestCaseDeepLink } from "../hooks/useTestCaseDeepLink"
import type { TestSetEditorFeatureFlags } from "../model"
import { consumeCoviTestSetPrompt } from "../utils/coviPromptSeed"
import {
  TestSetVersionHistoryProvider,
  useTestSetVersionHistory,
} from "../version-history/components/TestSetVersionHistoryContext"
import { TestSetVersionHistoryView } from "../version-history/components/TestSetVersionHistoryView"
import { TestSetAutoAddSection } from "./auto-add/TestSetAutoAddSection"
import { CoviTestSetEditorBridge } from "./CoviTestSetEditorBridge"
import { FindReplace } from "./FindReplace"
import { TestCaseList } from "./test-case-list"
import { TestSetAgentLink } from "./TestSetAgentLink"
import { TestSetDetailsRow } from "./TestSetDetailsRow"
import { TestSetEditorNavigationGuard } from "./TestSetEditorNavigationGuard"
import { TestSetExpectedBehaviorDrawer } from "./TestSetExpectedBehaviorDrawer"
import { TestSetHeader } from "./TestSetHeader"

interface TestSetEditorPageProps {
  testSetId: string
  featureFlags: TestSetEditorFeatureFlags
  initialMetadata?: {
    metadata?: Partial<TestSetRecord> & { display_name: string }
  }
}

const TestSetEditorContent: React.FC = () => {
  const {
    isLoadingMetadata,
    isLoadingTestCases,
    canUndo,
    testSetId,
    metadata,
    isInitialized,
    expectedBehaviorTestCaseId,
    closeExpectedBehaviorTest,
    setIsExpectedBehaviorTestRunning,
    pendingExpectedBehaviorTestCaseId,
    confirmExpectedBehaviorTestSwitch,
    cancelExpectedBehaviorTestSwitch,
    testCases,
    visibleTestCases,
    clearTestCaseFilters,
  } = useTestSetEditorContext()
  const router = useRouter()
  const versionHistory = useTestSetVersionHistory()
  const dispatch = useEventDispatcher()

  // The Version history action lives in the breadcrumb header (outside this provider); it reaches
  // the version-history mode through the editor event bus.
  useEventBus(EVENTS.OPEN_VERSION_HISTORY, () => {
    versionHistory.open()
  })

  const [isFindReplaceOpen, setIsFindReplaceOpen] = useState(false)
  const [findReplaceMode, setFindReplaceMode] = useState<"find" | "replace">(
    "find",
  )

  const isLoading = isLoadingMetadata || isLoadingTestCases
  const expectedBehaviorTestCase =
    testCases.find(({ id }) => id === expectedBehaviorTestCaseId) ?? null

  const selectTestCase = useCallback(
    (testCaseId: string) => dispatch(EVENTS.TEST_CASE_SELECT, { testCaseId }),
    [dispatch],
  )
  const testCaseIds = useMemo(() => testCases.map(({ id }) => id), [testCases])
  const visibleTestCaseIds = useMemo(
    () => visibleTestCases.map(({ id }) => id),
    [visibleTestCases],
  )
  useTestCaseDeepLink({
    isInitialized,
    testCaseIds,
    visibleTestCaseIds,
    selectTestCase,
    clearFilters: clearTestCaseFilters,
  })

  const coviSeed =
    typeof router.query.coviSeed === "string" ? router.query.coviSeed : null
  useEffect(() => {
    if (!isInitialized || !coviSeed) {
      return
    }
    const { coviSeed: _, ...rest } = router.query
    const navigation = buildWorkspaceAwareNavigation(
      router.asPath,
      router.pathname,
      rest,
    )
    void router.replace(navigation.href, navigation.as, { shallow: true })
    try {
      const prompt = consumeCoviTestSetPrompt(coviSeed)
      if (prompt) {
        openCoviWithPrompt(prompt)
      }
    } catch {
      // Session storage may be unavailable; leave the editor usable.
    }
  }, [coviSeed, isInitialized, router])

  // Keyboard shortcuts for undo (Cmd+Z) and find (Cmd+F)
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // A modal layer covers the editor, so its find and undo stand down.
      if (isModalLayerTarget(event.target)) {
        return
      }

      // Don't trigger if user is typing in an editor control (except when FindReplace is open)
      const isInInput = isKeyboardTypingTarget(event.target)

      // Check for Cmd+F (Mac) or Ctrl+F (Windows/Linux) - Find
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "f") {
        // Always prevent browser default when FindReplace is open
        // Or if user is not typing in an input (allowing FindReplace to handle it)
        if (isFindReplaceOpen || !isInInput) {
          event.preventDefault()
          event.stopPropagation()
          if (!isFindReplaceOpen) {
            setFindReplaceMode("find")
            setIsFindReplaceOpen(true)
          }
        }
        return
      }

      // Check for Cmd+Z (Mac) or Ctrl+Z (Windows/Linux) - Undo
      if ((event.metaKey || event.ctrlKey) && event.key === "z") {
        if (isInInput) {
          return
        }

        if (canUndo) {
          event.preventDefault()
          dispatch(EVENTS.UNDO, {})
        }
      }
    }

    // Use capture phase to ensure we handle these before other handlers
    window.addEventListener("keydown", handleKeyDown, true)
    return () => window.removeEventListener("keydown", handleKeyDown, true)
  }, [canUndo, dispatch, isFindReplaceOpen])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <Skeleton className="h-8 w-48 rounded-md" />
          <p className="text-sm text-coval-text-muted">Loading test set...</p>
        </div>
      </div>
    )
  }

  if (versionHistory.isActive) {
    return <TestSetVersionHistoryView />
  }

  return (
    <TestSetEditorNavigationGuard>
      {/* Declares the test set this editor is about. Read by
          app-covi/utils/viewContext — data-only, no module import. */}
      <div
        data-testid="test-set-editor"
        className="flex flex-col h-full overflow-hidden w-full relative"
        data-covi-view={
          testSetId
            ? JSON.stringify({
                kind: "test_set",
                id: testSetId,
                name: metadata.displayName || undefined,
              })
            : undefined
        }
      >
        <CoviTestSetEditorBridge />
        {isFindReplaceOpen && (
          <FindReplace
            isOpen={isFindReplaceOpen}
            onClose={() => setIsFindReplaceOpen(false)}
            initialMode={findReplaceMode}
          />
        )}
        <TestSetHeader />
        <div className="flex flex-1 min-h-0 h-full relative overflow-hidden">
          {/* The controls row and the grid share one platter, so the card opens
              at the controls rather than between them and the header row. */}
          <div
            className={cn(
              "flex flex-1 min-h-0 flex-col min-w-0 relative",
              TABLE_CARD_CLASS,
            )}
          >
            <TestSetDetailsRow />
            <TestSetAutoAddSection testSetId={testSetId}>
              <TestSetAgentLink />
              <div className="test-case-scroll-container flex-1 min-h-0 overflow-auto relative overscroll-contain">
                <TestCaseList />
              </div>
            </TestSetAutoAddSection>
          </div>
          {expectedBehaviorTestCase && (
            <TestSetExpectedBehaviorDrawer
              testCase={expectedBehaviorTestCase}
              testSetId={testSetId}
              onClose={closeExpectedBehaviorTest}
              onRunningChange={setIsExpectedBehaviorTestRunning}
            />
          )}
        </div>
        <AlertDialog
          open={Boolean(pendingExpectedBehaviorTestCaseId)}
          onOpenChange={(open) => {
            if (!open) {
              cancelExpectedBehaviorTestSwitch()
            }
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>A test is in progress</AlertDialogTitle>
              <AlertDialogDescription>
                Do you want to navigate away from this test?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="pt-4">
              <AlertDialogCancel asChild>
                <Button type="button" variant="secondary">
                  Stay here
                </Button>
              </AlertDialogCancel>
              <AlertDialogAction asChild>
                <Button
                  type="button"
                  variant="default"
                  onClick={confirmExpectedBehaviorTestSwitch}
                >
                  Navigate away
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </TestSetEditorNavigationGuard>
  )
}

export const TestSetEditorPage: React.FC<TestSetEditorPageProps> = ({
  testSetId,
  featureFlags,
  initialMetadata,
}) => {
  return (
    <TestSetEditorProvider
      testSetId={testSetId}
      featureFlags={featureFlags}
      initialMetadata={initialMetadata}
    >
      <TestSetSaveProvider>
        <FindReplaceProvider>
          <TestSetVersionHistoryProvider testSetId={testSetId}>
            <TestSetEditorContent />
          </TestSetVersionHistoryProvider>
        </FindReplaceProvider>
      </TestSetSaveProvider>
    </TestSetEditorProvider>
  )
}
